import React, { useState } from 'react';
import { Container, Card, Form, Button } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import useAppToast from 'src/hooks/useAppToast';
import { updateForgottenPasswordAsync } from 'src/redux/actions/authActions';
import { Link, useNavigate } from 'react-router-dom';

const ResetPasswordPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { forgetPasswordToken, forgetPasswordTokenLoading } = useAppSelector((state) => state.auth);
  const { successToast, errorToast } = useAppToast();

  const initialValues = {
    forgetPasswordToken: '',
    password: '',
    confirm_password: '',
  };

  const passwordSchema = Yup.object().shape({
    password: Yup.string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>-])[a-zA-Z\d!@#$%^&*(),.?":{}|<>-]{8,}$/,
        'Use 8 or more characters with at least one uppercase letter, one lowercase letter, one special character, and one number'
      )
      .required('Password is required'),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Confirm password is required'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: passwordSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        if (!forgetPasswordToken) {
          return errorToast('Forget password token is missing');
        }
        dispatch(updateForgottenPasswordAsync({
          forgot_password_token: forgetPasswordToken,
          password: values.password,
          confirm_password: values.confirm_password,
        })).then((response) => {
          if (response?.payload?.status_code === 200) {
            successToast(response?.payload?.massage);
            setTimeout(() => {
              navigate("/login");
            }, 1000);
          } else {
            errorToast(response?.payload?.massage);
          }
        });
        resetForm();
      } catch (error) {
        errorToast('Failed to update password');
      }
    },
  });

  return (
    <Container className="py-5 d-flex justify-content-center align-items-center">
      <style>
        {`
          .floating-label-group {
            position: relative;
            margin-bottom: 1.5rem;
          }

          .floating-label-group input {
            border: 1px solid #ced4da;
            border-radius: 0.5rem;
            padding: 1rem 0.75rem 1rem 0.75rem;
            font-size: 1rem;
            width: 100%;
            transition: border-color 0.3s, padding-top 0.3s;
            background: none;
          }

          .floating-label-group label {
            position: absolute;
            top: 1rem;
            left: 0.75rem;
            font-size: 1rem;
            color: #6c757d;
            transition: 0.2s ease all;
            pointer-events: none;
            background: white;
            padding: 0 0.5rem;
            transform-origin: left top;
          }

          .floating-label-group input:focus {
            outline: none;
            border-color: #0d6efd;
          }

          .floating-label-group input:focus + label,
          .floating-label-group input:not(:placeholder-shown) + label {
            top: -0.7rem;
            left: 0.6rem;
            font-size: 0.8rem;
            color: #0d6efd;
            padding: 0 0.25rem;
          }

          .card-style {
            background-color: #ffffff;
            border-radius: 1rem;
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
            width: 100%;
            max-width: 500px;
          }

          @media (max-width: 576px) {
            .card-style {
              padding: 1.5rem !important;
              border-radius: 0.75rem;
            }

            .text-muted {
              font-size: 0.9rem;
            }
          }
        `}
      </style>

      <Card className="p-4 card-style">
        {forgetPasswordTokenLoading ? (
          <div className="text-center mb-4">
            <span className="spinner-border spinner-border-sm"></span>
            <span className="ms-2">Loading token...</span>
          </div>
        ) : (
          <>
            <div className="text-center mb-3">
              <h4 className="fw-bold mb-2">Reset Password</h4>
              <p className="text-muted">
                Enter your new password below to reset your account.
              </p>
            </div>

            <Form onSubmit={formik.handleSubmit}>
              <div className="floating-label-group">
                <Form.Control
                  type="password"
                  placeholder=" "
                  required
                  id="password"
                  {...formik.getFieldProps('password')}
                  isInvalid={formik.touched.password && !!formik.errors.password}
                />
                <Form.Label>Password</Form.Label>
                {formik.touched.password && formik.errors.password && (
                  <div className="invalid-feedback">{formik.errors.password}</div>
                )}
              </div>

              <div className="floating-label-group">
                <Form.Control
                  type="password"
                  placeholder=" "
                  required
                  id="confirm_password"
                  {...formik.getFieldProps('confirm_password')}
                  isInvalid={formik.touched.confirm_password && !!formik.errors.confirm_password}
                />
                <Form.Label>Confirm Password</Form.Label>
                {formik.touched.confirm_password && formik.errors.confirm_password && (
                  <div className="invalid-feedback">{formik.errors.confirm_password}</div>
                )}
              </div>

              <Button type="submit" variant="primary" className="w-100 mb-3" disabled={formik.isSubmitting}>
                {formik.isSubmitting ? 'Resetting...' : 'Reset Password'}
              </Button>

              <div className="text-center">
                <Link to="/auth/login-page" className="text-decoration-none">
                  Back to Login
                </Link>
              </div>
            </Form>
          </>
        )}
      </Card>
    </Container>
  );
};

export default ResetPasswordPage;
